/**
 * useNotificationStream — SSE hook for real-time notifications AND live
 * comment events on subscribed posts.
 *
 * Usage:
 *   const { unread, lastNotification, lastCommentEvent } =
 *       useNotificationStream({ postIds: [p1, p2] });
 *
 * Falls back to 30s polling when EventSource is missing.
 */
import { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useNotificationStream({ postIds } = {}) {
  const [unread, setUnread] = useState(0);
  const [lastNotification, setLastNotification] = useState(null);
  const [lastCommentEvent, setLastCommentEvent] = useState(null);
  const pollRef = useRef(null);

  // Stable key for the postIds so the effect only re-runs when they actually change
  const subscribeKey = useMemo(
    () => Array.from(new Set(postIds || [])).slice(0, 20).sort().join(","),
    [postIds]
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const startPolling = () => {
      if (pollRef.current) return;
      const tick = async () => {
        try {
          const r = await axios.get(`${API}/notifications?unread=true&limit=1`);
          if (typeof r.data.unread === "number") setUnread(r.data.unread);
        } catch { /* silent */ }
      };
      tick();
      pollRef.current = setInterval(tick, 30000);
    };

    if (typeof window === "undefined" || !window.EventSource) {
      startPolling();
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }

    const qs = new URLSearchParams({ token });
    if (subscribeKey) qs.set("posts", subscribeKey);
    const url = `${API}/notifications/stream?${qs.toString()}`;
    let closed = false;
    const es = new EventSource(url);

    es.addEventListener("hello", (e) => {
      try {
        const d = JSON.parse(e.data);
        if (typeof d.unread === "number") setUnread(d.unread);
      } catch { /* ignore */ }
    });
    es.addEventListener("unread", (e) => {
      try {
        const d = JSON.parse(e.data);
        if (typeof d.unread === "number") setUnread(d.unread);
      } catch { /* ignore */ }
    });
    es.addEventListener("notification", (e) => {
      try {
        const notif = JSON.parse(e.data);
        setLastNotification(notif);
        setUnread((prev) => prev + 1);
      } catch { /* ignore */ }
    });
    const relayComment = (e) => {
      try { setLastCommentEvent(JSON.parse(e.data)); } catch { /* ignore */ }
    };
    es.addEventListener("new_comment", relayComment);
    es.addEventListener("comment_edited", relayComment);
    es.addEventListener("comment_deleted", relayComment);
    es.onerror = () => { if (!closed) startPolling(); };

    return () => {
      closed = true;
      try { es.close(); } catch { /* ignore */ }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [subscribeKey]);

  return { unread, lastNotification, lastCommentEvent, setUnread };
}
